import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFormContext } from '../context/FormContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import CTAButton from '../components/CTAButton';
import SecurityBadges from '../components/SecurityBadges';

const KnownInfoSelection: React.FC = () => {
  const { formData, nextStep } = useFormContext();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState<string | null>(null);

  const handleSelection = async (selection: string) => {
    setIsLoading(selection);
    
    try {
      await nextStep({ knownInfo: selection });
      
      // Navigate based on selection
      switch(selection) {
        case 'address':
          navigate('/address-details');
          break;
        case 'birthdate':
          navigate('/birthdate');
          break;
        case 'both':
          navigate('/both-info');
          break;
        case 'none':
          navigate('/no-info');
          break;
        default:
          navigate('/address-details');
      }
    } catch (error) {
      console.error('Error saving selection:', error);
      setIsLoading(null);
    }
  };

  const handleBack = () => {
    navigate('/address-data');
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <main className="flex-grow container mx-auto px-4 py-8 max-w-3xl">
        <div className="form-container fade-in">
          <h2 className="text-2xl text-center font-semibold text-deep-blue mb-8">
            Folgende Angaben sind mir bekannt (bitte auswählen):
          </h2>
          
          <div className="space-y-4">
            <button
              onClick={() => handleSelection('address')}
              disabled={isLoading !== null}
              className={`w-full p-4 text-left border rounded-lg transition-colors flex items-center
                ${isLoading === 'address' ? 'bg-blue-50 border-deep-blue' : 'hover:bg-gray-50'}
                ${isLoading !== null ? 'cursor-not-allowed opacity-70' : ''}`}
            >
              <span className="flex-shrink-0 mr-3">
                {isLoading === 'address' ? (
                  <div className="w-6 h-6 border-2 border-deep-blue border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-deep-blue" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                  </svg>
                )}
              </span>
              Vormalige Straße & Hausnummer
            </button>
            
            <button
              onClick={() => handleSelection('birthdate')}
              disabled={isLoading !== null}
              className={`w-full p-4 text-left border rounded-lg transition-colors flex items-center
                ${isLoading === 'birthdate' ? 'bg-blue-50 border-deep-blue' : 'hover:bg-gray-50'}
                ${isLoading !== null ? 'cursor-not-allowed opacity-70' : ''}`}
            >
              <span className="flex-shrink-0 mr-3">
                {isLoading === 'birthdate' ? (
                  <div className="w-6 h-6 border-2 border-deep-blue border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-deep-blue" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                )}
              </span>
              Geburtsdatum
            </button>
            
            <button
              onClick={() => handleSelection('both')}
              disabled={isLoading !== null}
              className={`w-full p-4 text-left border rounded-lg transition-colors flex items-center
                ${isLoading === 'both' ? 'bg-blue-50 border-deep-blue' : 'hover:bg-gray-50'}
                ${isLoading !== null ? 'cursor-not-allowed opacity-70' : ''}`}
            >
              <span className="flex-shrink-0 mr-3">
                {isLoading === 'both' ? (
                  <div className="w-6 h-6 border-2 border-deep-blue border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-deep-blue" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </span>
              Beides bekannt
            </button>
            
            <button
              onClick={() => handleSelection('none')}
              disabled={isLoading !== null}
              className={`w-full p-4 text-left border rounded-lg transition-colors flex items-center
                ${isLoading === 'none' ? 'bg-blue-50 border-deep-blue' : 'hover:bg-gray-50'}
                ${isLoading !== null ? 'cursor-not-allowed opacity-70' : ''}`}
            >
              <span className="flex-shrink-0 mr-3">
                {isLoading === 'none' ? (
                  <div className="w-6 h-6 border-2 border-deep-blue border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-deep-blue" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                  </svg>
                )}
              </span>
              Straße/Hausnummer & Geburtsdatum unbekannt
            </button>
          </div>
          
          <div className="mt-8">
            <CTAButton 
              text="Zurück" 
              back={true} 
              onClick={handleBack}
              className={`w-full ${isLoading !== null ? 'opacity-70 cursor-not-allowed' : ''}`}
              type="button"
            />
          </div>
        </div>
        
        <SecurityBadges />
      </main>
      
      <Footer />
    </div>
  );
};

export default KnownInfoSelection;
